import React, { useEffect } from 'react'
import {
  Box,
  Button,
  makeStyles,
  Paper,
  styled,
  Typography
} from '@material-ui/core'
import FormWrapperBottomNav, {
  IFormWrapperBottomNavMenus
} from './FormWrapperBottomNav'
import FormWrapperTabs from './FormWrapperTabs'
import FormMoreOptions from './FormMoreOptions'
import { IHealthAndSafetyField } from '../..'
import FormWrapperTabVisibilityObserver from './FormWrapperTabVisibilityObserver'
import { sanatizeForElementId } from '../../utils/stringUtil'

const CustomButton = styled(Button)(() => ({
  // Default contained styles
  backgroundColor: '#223d79',
  color: 'white',
  '&:hover': {
    backgroundColor: '#1b315f'
  },
  '&:disabled': {
    backgroundColor: '#cccccc',
    color: '#666666'
  },

  // Outlined variant styles
  '&.outlined': {
    backgroundColor: 'transparent',
    color: '#223d79',
    border: '2px solid #223d79',
    '&:hover': {
      backgroundColor: 'rgba(34, 61, 121, 0.04)',
      borderColor: '#1b315f'
    },
    '&:disabled': {
      borderColor: '#cccccc',
      color: '#666666'
    }
  }
}))

const useStyles = makeStyles((theme) => ({
  appWrapper: {
    marginTop: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    // maxWidth: 800,
    position: 'relative'
  },
  appWrapperDialog: {
    marginTop: 64,
    [theme.breakpoints.down('xs')]: {
      marginTop: 52
    }
  },
  header: {
    background: theme.palette.primary.main,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '8px 16px',
    color: '#FFF'
  },
  appWrapperContent: {
    // maxHeight: 'calc(100vh - 56px - 61px - 64px)',
    overflow: 'auto'
  },
  headerActionButton: {
    textAlign: 'left',
    [theme.breakpoints.up('md')]: {
      textAlign: 'right' // override for md and up
    }
  },
  actionButton: {
    textAlign: 'right'
  },
  bottomNavBox: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0
  }
}))

export interface IFormFields {
  [key: string]: {
    label: string
    fields: IHealthAndSafetyField[]
  }[]
}

export interface IFormWrapperDetails {
  title: string
  assigneeTypeLabel: string
  assigneeTypeValue: string
  categoryLabel: string
  categoryValue: string
  saveStatus: string
}

interface IFormWrapperProps {
  children: React.ReactNode
  details: Partial<IFormWrapperDetails>
  showTabs?: boolean
  tabs: {
    label: string
  }[]
  tabMenuLabel?: string
  customTabChange?: (newValue: number) => void
  showTabNextAndPrevious?: boolean
  bottomNavMenus: IFormWrapperBottomNavMenus[]
  onChangeBottomNav: (value: any) => void
  selectedBottomNav?: number
  showMoreOptions?: boolean
  options?: {
    label: string
    icon?: any
    onClick: () => void
    disabled?: boolean
  }[]
  selectedOption?: string
  showGroupings?: boolean
  ActionButton?: React.ReactNode
  CustomBottomDetails?: React.ReactNode
  tabObserverAttribute?: string
}

const FormWrapper = ({
  children,
  showTabs = true,
  tabs,
  tabMenuLabel,
  details,
  customTabChange,
  showTabNextAndPrevious,
  bottomNavMenus,
  onChangeBottomNav,
  selectedBottomNav,
  showMoreOptions = false,
  options = [],
  selectedOption,
  showGroupings = true,
  ActionButton,
  CustomBottomDetails,
  tabObserverAttribute = '[data-watch-id]'
}: IFormWrapperProps) => {
  const classes = useStyles()

  const [selectedTab, setSelectedTab] = React.useState(0)
  const [page, setPage] = React.useState(0)
  useEffect(() => {
    const element = document.getElementById('assignee')
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }, [tabs])

  const onChangeTab = (_event: unknown, newValue: number) => {
    setSelectedTab(newValue)
    if (customTabChange) customTabChange(newValue)
    const element = document.getElementById(
      sanatizeForElementId(tabs[newValue].label)
    )
    if (element) {
      //   const y = element.getBoundingClientRect(   ).top + window.pageYOffset
      element.scrollIntoView({
        behavior: 'smooth',
        inline: 'start'
      })
    }
  }

  const onChangeNav = (value: any) => {
    setSelectedTab(0)
    onChangeBottomNav(value)
  }

  const handleNext = () => {
    setSelectedTab(0)
    setPage(page + 1)
    onChangeBottomNav(page + 1)
  }

  const handlePrev = () => {
    setSelectedTab(0)
    setPage(page - 1)
    onChangeBottomNav(page - 1)
  }

  return (
    <div id='appWrapper' className={classes.appWrapper}>
      <div className={classes.header}>
        <Box
          display='flex'
          alignItems='center'
          justifyContent={'space-between'}
          flexDirection={'row'}
        >
          <Typography variant='h5'>{details?.title ?? 'Unknown'}</Typography>
          {showMoreOptions && (
            <FormMoreOptions
              options={options}
              selectedOption={selectedOption}
            />
          )}
        </Box>
        <Box display={'flex'} flexDirection={'column'}>
          <Typography
            style={{ marginBottom: 4, textTransform: 'capitalize' }}
            variant='body1'
          >
            {details?.assigneeTypeLabel ?? 'Assignee Type'}:{' '}
            {details?.assigneeTypeValue ?? 'Unknown'}
          </Typography>
          {details.categoryLabel && (
            <Typography
              style={{ marginBottom: 4, textTransform: 'capitalize' }}
              variant='subtitle1'
            >
              {details.categoryLabel}: {details?.categoryValue ?? ''}
            </Typography>
          )}
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            {details.saveStatus && (
              <Typography
                style={{ marginBottom: 4, textTransform: 'capitalize' }}
                variant='subtitle1'
              >
                {details.saveStatus}
              </Typography>
            )}
            {ActionButton && (
              <Box
                style={{
                  textTransform: 'capitalize',
                  marginLeft: 'auto'
                }}
              >
                {ActionButton}
              </Box>
            )}
          </Box>
          {CustomBottomDetails && <Box>{CustomBottomDetails}</Box>}
        </Box>
      </div>
      <FormWrapperTabs
        labelId='incident-form-tabs'
        onChange={onChangeTab}
        value={selectedTab}
        selectionFollowsFocus={true}
        tabs={tabs}
        tabMenuLabel={tabMenuLabel}
        showTabs={showTabs}
      >
        <div id='appWrapperContent' className={classes.appWrapperContent}>
          {selectedTab > -1 && tabs.length > 0 && (
            <FormWrapperTabVisibilityObserver
              selector={tabObserverAttribute}
              selectedTab={selectedTab}
              onVisible={(id) => {
                setSelectedTab(Number(id))
              }}
              tabs={tabs}
            />
          )}
          {children}
        </div>
        {showTabNextAndPrevious && (
          <Box
            padding={2}
            display={'flex'}
            justifyContent={'end'}
            marginBottom={'56px'}
          >
            {selectedTab > 0 && (
              <CustomButton
                onClick={() => onChangeTab(undefined, selectedTab - 1)}
                className='outlined'
                color='secondary'
                style={{ marginRight: 10 }}
              >
                Previous
              </CustomButton>
            )}

            {selectedTab < tabs.length - 1 && (
              <CustomButton
                variant='contained'
                color='secondary'
                onClick={() => onChangeTab(undefined, selectedTab + 1)}
              >
                Next
              </CustomButton>
            )}
          </Box>
        )}
        <Box className={classes.bottomNavBox}>
          {showGroupings ? (
            <FormWrapperBottomNav
              menus={bottomNavMenus}
              onChange={onChangeNav}
              value={selectedBottomNav}
            />
          ) : (
            <Box
              component={Paper}
              padding={2}
              display={'flex'}
              justifyContent={'end'}
            >
              <CustomButton
                onClick={handlePrev}
                className='outlined'
                disabled={page === 0}
                color='secondary'
                style={{ marginRight: 10 }}
              >
                Previous
              </CustomButton>

              <CustomButton
                variant='contained'
                color='secondary'
                onClick={handleNext}
                disabled={page === bottomNavMenus.length - 1}
              >
                Next
              </CustomButton>
            </Box>
          )}
        </Box>
      </FormWrapperTabs>
    </div>
  )
}

export default FormWrapper
